/* ============================================================
   logo-banner.js
   Chemin : assets/logo-banner.js
   Chargé avec defer — le DOM est donc prêt à l'exécution.
   ============================================================ */

(function () {
  'use strict';

  /**
   * Initialise une instance de logo banner.
   * @param {HTMLElement} section
   */
  function initLogoBanner(section) {
    const track   = section.querySelector('.logo-banner__track');
    const wrapper = section.querySelector('.logo-banner__track-wrapper');

    if (!track || !wrapper) return;

    // ── Lecture des options depuis les data-attributes ──────────
    const direction    = section.dataset.direction    || 'left';
    const pauseOnHover = section.dataset.pauseOnHover === 'true';

    // ── Direction ───────────────────────────────────────────────
    if (direction === 'right') {
      track.classList.add('logo-banner__track--reverse');
    }

    // ── Pause au survol ─────────────────────────────────────────
    if (pauseOnHover) {
      section.classList.add('logo-banner--pause-on-hover');
    }

    // ── Détection fond sombre → mix-blend-mode: screen ─────────
    //    On lit la couleur de fond calculée par le navigateur.
    //    Si la luminance perçue est < 0.5, on bascule en mode dark.
    detectDarkScheme(section);

    // ── Seamless loop : duplication dynamique si nécessaire ─────
    //    La duplication est déjà faite en Liquid, mais si les logos
    //    sont peu nombreux et trop larges, on peut avoir besoin de
    //    plus de copies pour couvrir toute la largeur du wrapper.
    ensureEnoughCopies(track, wrapper);

    // ── Drag to scroll ──────────────────────────────────────────
    initDrag(track, wrapper);

    // ── Recalcul au resize ──────────────────────────────────────
    const resizeObserver = new ResizeObserver(() => {
      ensureEnoughCopies(track, wrapper);
    });
    resizeObserver.observe(wrapper);
  }

  /**
   * Drag to scroll : l'utilisateur peut faire glisser la bande à la souris.
   * — Pendant le drag : animation pausée, offset CSS mis à jour manuellement.
   * — Après le drag : animation reprend avec une inertie (momentum).
   *
   * @param {HTMLElement} track
   * @param {HTMLElement} wrapper
   */
  function initDrag(track, wrapper) {
    var isDragging   = false;
    var startX       = 0;       // position souris au mousedown
    var currentOffset = 0;      // offset CSS courant (translateX)
    var lastX        = 0;       // pour calculer la vélocité
    var velocity     = 0;       // px/frame au moment du mouseup
    var rafId        = null;    // requestAnimationFrame en cours

    // Lit l'offset translateX courant de la track (depuis la matrix CSS)
    function getTranslateX() {
      var style = window.getComputedStyle(track);
      var matrix = new DOMMatrix(style.transform);
      return matrix.m41; // translateX
    }

    // Applique un translateX sans toucher à l'animation CSS
    function setTranslateX(x) {
      // On normalise pour rester dans la boucle seamless (−50% de scrollWidth)
      var half = track.scrollWidth / 2;
      x = ((x % half) - half) % half; // toujours négatif, dans [−half, 0]
      track.style.transform = 'translateX(' + x + 'px)';
    }

    function onMouseDown(e) {
      if (e.button !== 0) return; // clic gauche uniquement
      isDragging = true;
      startX = e.clientX;
      lastX  = e.clientX;
      currentOffset = getTranslateX();
      velocity = 0;

      // Pause l'animation CSS et verrouille la position actuelle
      track.style.animationPlayState = 'paused';
      track.style.transform = 'translateX(' + currentOffset + 'px)';
      track.style.animationName = 'none'; // désactive complètement pendant le drag

      wrapper.classList.add('logo-banner__track-wrapper--dragging');
      if (rafId) cancelAnimationFrame(rafId);

      e.preventDefault();
    }

    function onMouseMove(e) {
      if (!isDragging) return;
      var delta = e.clientX - startX;
      velocity  = e.clientX - lastX; // vélocité instantanée
      lastX     = e.clientX;
      setTranslateX(currentOffset + delta);
    }

    function onMouseUp() {
      if (!isDragging) return;
      isDragging = false;
      wrapper.classList.remove('logo-banner__track-wrapper--dragging');

      // Inertie : continue sur la lancée puis reprend l'animation auto
      applyMomentum(getTranslateX(), velocity);
    }

    function applyMomentum(offset, vel) {
      var friction = 0.92; // décélération (0 = stop immédiat, 1 = glisse infini)

      function step() {
        vel *= friction;
        offset += vel;
        setTranslateX(offset);

        if (Math.abs(vel) > 0.5) {
          rafId = requestAnimationFrame(step);
        } else {
          // Vélocité négligeable → reprend l'animation CSS depuis la position actuelle
          resumeAnimation(offset);
        }
      }

      rafId = requestAnimationFrame(step);
    }

    function resumeAnimation(fromOffset) {
      // On recalcule le delay pour que l'animation reprenne exactement là où on s'est arrêté.
      // La keyframe va de 0 à -50% (scrollWidth/2), donc on convertit l'offset en pourcentage.
      var half = track.scrollWidth / 2;
      var progress = Math.abs(fromOffset % half) / half; // valeur entre 0 et 1

      var speed    = parseFloat(track.style.animationDuration) ||
                     parseFloat(getComputedStyle(track).getPropertyValue('--lb-speed')) || 35;

      // delay négatif = on démarre au milieu de l'animation
      var delay = -(progress * speed);

      track.style.transform        = '';
      track.style.animationName    = 'logo-scroll';
      track.style.animationDelay   = delay + 's';
      track.style.animationPlayState = 'running';
    }

    // ── Événements souris ───────────────────────────────────────
    wrapper.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup',   onMouseUp);

    // ── Touch (mobile) ──────────────────────────────────────────
    wrapper.addEventListener('touchstart', function (e) {
      onMouseDown({ button: 0, clientX: e.touches[0].clientX, preventDefault: function(){} });
    }, { passive: true });

    wrapper.addEventListener('touchmove', function (e) {
      onMouseMove({ clientX: e.touches[0].clientX });
    }, { passive: true });

    wrapper.addEventListener('touchend', onMouseUp);
  }

  /**
   * Détecte si le fond de la section est sombre et ajoute la classe
   * .logo-banner--dark pour basculer mix-blend-mode en "screen".
   *
   * Fonctionnement :
   *  1. getComputedStyle lit la couleur de fond résolue par le navigateur
   *     après application du color-scheme Shopify.
   *  2. On calcule la luminance relative (formule W3C WCAG 2.1).
   *  3. Si luminance < 0.5 → fond sombre → classe "logo-banner--dark".
   *
   * @param {HTMLElement} section
   */
  function detectDarkScheme(section) {
    const bg = window.getComputedStyle(section).backgroundColor;

    // Extrait r, g, b depuis "rgb(r, g, b)" ou "rgba(r, g, b, a)"
    const match = bg.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (!match) return;

    const r = Number(match[1]);
    const g = Number(match[2]);
    const b = Number(match[3]);

    // Luminance relative W3C (sRGB linéarisé)
    function toLinear(c) {
      var s = c / 255;
      return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    }

    var luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);

    if (luminance < 0.5) {
      section.classList.add('logo-banner--dark');
    } else {
      section.classList.remove('logo-banner--dark');
    }
  }

  /**
   * Vérifie que la piste est au moins 2× plus large que le wrapper.
   * Si ce n'est pas le cas, on duplique les items originaux.
   *
   * @param {HTMLElement} track
   * @param {HTMLElement} wrapper
   */
  function ensureEnoughCopies(track, wrapper) {
    // On ne travaille que sur les items "originaux" (non aria-hidden)
    const originals = Array.from(
      track.querySelectorAll('.logo-banner__item:not([aria-hidden="true"])')
    );

    if (originals.length === 0) return;

    const wrapperWidth = wrapper.offsetWidth;

    // Supprime les clones précédents pour repartir proprement
    track.querySelectorAll('.logo-banner__item--clone').forEach(function (el) { el.remove(); });
    track.querySelectorAll('.logo-banner__separator--clone').forEach(function (el) { el.remove(); });

    // Tant que la piste est moins large que 2× le wrapper, on ajoute des copies
    var safety = 0;
    while (track.scrollWidth < wrapperWidth * 2 && safety < 10) {
      originals.forEach(function (item) {
        const clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.classList.add('logo-banner__item--clone');
        // Désactive les liens dans les clones pour l'accessibilité
        clone.querySelectorAll('a').forEach(function (a) { a.setAttribute('tabindex', '-1'); });
        track.appendChild(clone);
      });
      safety++;
    }
  }

  // ── Point d'entrée ──────────────────────────────────────────

  // Initialise toutes les sections présentes sur la page
  document.querySelectorAll('.logo-banner[data-section-id]').forEach(initLogoBanner);

  // Compatibilité Shopify : ré-initialise après les events du Customizer
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      const section = e.target.querySelector('.logo-banner[data-section-id]');
      if (section) initLogoBanner(section);
    });

    // Ré-évalue le color-scheme si l'utilisateur change le thème dans le Customizer
    document.addEventListener('shopify:section:select', function (e) {
      const section = e.target.querySelector('.logo-banner[data-section-id]');
      if (section) detectDarkScheme(section);
    });

    document.addEventListener('shopify:section:reorder', function () {
      document.querySelectorAll('.logo-banner[data-section-id]').forEach(initLogoBanner);
    });
  }
})();